import { filterClearTracking, filterOptionClickTracking } from '../../components/GTMEvents';
import { Sidebar } from '../../components/Sidebar';
import { SIZE_ORDER } from '../../utils/sizeOrder';

const PRICE_CACHE_KEY = 'plp_price_cache';
const PRICE_CACHE_TTL = 24 * 60 * 60 * 1000; // 24h

const US_PRICE_RANGES = [
  { min: 0, max: 5, label: '< $5' },
  { min: 5, max: 20, label: '$5 - $20' },
  { min: 20, max: 30, label: '$20 - $30' },
  { min: 30, max: 50, label: '$30 - $50' },
  { min: 50, max: 100, label: '$50 - $100' },
  { min: 100, max: 200, label: '$100 - $200' },
  { min: 200, max: Infinity, label: '$200 +' },
];

function savePriceCache(country, min, max) {
  const payload = {
    timestamp: Date.now(),
    country,
    min,
    max,
  };

  localStorage.setItem(PRICE_CACHE_KEY, JSON.stringify(payload));
}

function getPriceCache(country) {
  const raw = localStorage.getItem(PRICE_CACHE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);

    const expired = Date.now() - parsed.timestamp > PRICE_CACHE_TTL;

    if (expired || parsed.country !== country) {
      localStorage.removeItem(PRICE_CACHE_KEY);
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export class Filter extends Sidebar {
  constructor(sidebarId, overlayId) {
    super(sidebarId, overlayId);
    this.filtersContainer = this.node?.querySelector('#filters');
    this.searchSummary = this.node?.querySelector('.plp-c-filter__search-summary');
    this.searchTitle = this.node?.querySelector('.plp-c-filter__search-title');
    this.selectedFilters = {};
    this.facetContainers = new Map();
    this.scrollAnchor = null;
    this.country = window.Shopify?.country || 'US';
    this.closedFacets = this.#getClosedFacets();

    // Maps "facetName:value" → display_name for human-readable removable tags
    this.displayNameMap = new Map();

    // Removable tags containers
    this.sidebarTagsWrapper = this.node?.querySelector('.plp-c-filter__removable-tags-wrapper');
    this.sidebarTagsContainer = this.node?.querySelector('.plp-c-filter__removable-tags');
    this.mobileTagsWrapper = document.querySelector('.removable_tags_wrapper');
    this.mobileTagsContainer = document.querySelector('.removable_tags_container');

    // Color families metaobject data (hex codes and swatch images)
    this.colorFamilies = this.#parseColorFamilies();

    // Bind clear-all buttons via data attributes instead of inline onclick
    document.querySelectorAll('[data-clear-all-filters]').forEach((btn) => {
      btn.addEventListener('click', () => this.#clearAllFilters());
    });
  }

  /**
   * Register an external container to render a specific facet into (e.g. a mobile modal).
   * @param {string} facetName - the facet name as returned by Constructor API (e.g. 'size')
   * @param {HTMLElement} container - the DOM element to render the facet options into
   */
  registerFacetContainer(facetName, container) {
    if (container) this.facetContainers.set(facetName, container);
  }

  /**
   * Update the result count displayed on the action button and other action buttons (e.g in mobile modals).
   * Hides the count summary when count is 0 so the button reads just "VIEW RESULTS".
   * @param {number} count - total number of results
   */
  updateResultCount(count) {
    if (!this.searchTitle || !this.searchSummary) return;

    const hide = !count;

    this.searchTitle.textContent = count;
    this.searchSummary.style.display = hide ? 'none' : '';

    // Update counts on action buttons in mobile modals
    for (const container of this.facetContainers.values()) {
      const resultsPod = container.querySelector('.plp-c-mobile-modal__results-summary');
      const resultsLabel = container.querySelector('.plp-c-mobile-modal__results-title');

      resultsPod.style.display = hide ? 'none' : '';
      resultsLabel.textContent = count;
    }
  }

  /**
   * Register a callback invoked with active filters whenever a selection changes
   * @param {function} callback - receives the active filters object
   */
  onFilterChange(callback) {
    this.onChange = callback;
  }

  /**
   * Render Constructor.io facets into the sidebar filters container
   * @param {Array} facets - facets array from the Constructor API response
   */
  renderFacets(facets) {
    if (!this.filtersContainer) return;

    this.filtersContainer.innerHTML = '';

    facets.forEach((facet) => {
      if (facet.options) {
        facet.options = facet.options.filter((opt) => opt.count > 0);
      }
    });

    const isIntl = this.country !== 'US';
    const priceFacet = `price${this.country}`;
    const activeFacets = facets.filter((facet) => {
      // Filter out compareAtPrice facets
      if (facet.name.includes('compareAtPrice')) return false;

      // For international: only show price${country}, hide priceUS
      // For US: only show priceUS, hide any other price facets
      if (facet.name.startsWith('price')) {
        if (isIntl) return facet.name === priceFacet;
        return facet.name === 'priceUS';
      }
      return facet.options?.length;
    });

    activeFacets.forEach((facet) => {
      const isPriceFacet = facet.name.startsWith('price') && !facet.name.includes('compareAtPrice');
      const details = document.createElement('details');
      details.className = 'plp-c-filter__section';
      details.dataset.facetName = facet.name;

      // All groups open by default; only closed if user explicitly closed them
      if (!this.closedFacets.has(facet.name)) details.setAttribute('open', '');

      // Track user toggle: close → add to localStorage, re-open → remove
      details.addEventListener('toggle', () => {
        if (details.open) {
          this.closedFacets.delete(facet.name);
        } else {
          this.closedFacets.add(facet.name);
        }
        this.#saveClosedFacets();
      });

      const displayName = isPriceFacet ? 'Price' : facet.display_name;

      const summary = document.createElement('summary');
      summary.className = 'plp-c-filter__label-container';
      summary.innerHTML = `
        <span class="plp-c-filter__label">
          <span>${displayName}</span>
          <span class="plp-c-filter__selected-count">
            <span class="plp-c-filter__selected-count-badge"></span> selected
          </span>
        </span>
        <i class="fa fa-angle-down"></i>
      `;

      const isSize = facet.name === 'size';
      const isColor = facet.name === 'color_family';
      const optionsWrapper = document.createElement('div');
      if (isPriceFacet) {
        this.#renderPriceOptions(facet, optionsWrapper);
      } else if (isColor) {
        this.#renderColorOptions(facet, optionsWrapper);
      } else if (isSize) {
        this.#renderSizeOptions(facet, optionsWrapper);
      } else {
        this.#renderCheckboxOptions(facet, optionsWrapper);
      }

      details.appendChild(summary);
      details.appendChild(optionsWrapper);

      this.filtersContainer.appendChild(details);
    });

    this.#renderFacetContainers(facets);
    this.#restoreSelections();
    this.#updateSelectedCounts();
    this.#renderRemovableTags();
    this.filtersContainer.style.display = 'block';

    // Restore sidebar scroll position after re-render
    const sidebar = this.filtersContainer.closest('aside');
    if (sidebar) {
      if (this.savedScrollTop != null) {
        sidebar.scrollTop = this.savedScrollTop;
        this.savedScrollTop = null;
      } else if (this.scrollAnchor) {
        const { facetName, offsetFromViewport } = this.scrollAnchor;
        const section = this.filtersContainer.querySelector(`[data-facet-name="${facetName}"]`);
        if (section) {
          const newTop = section.getBoundingClientRect().top;
          sidebar.scrollBy({ top: newTop - offsetFromViewport });
        }
      }
    }
  }

  /**
   * Render individual facets into their registered external containers (mobile modals).
   * @param {Array} facets - facets array from the Constructor API response
   */
  #renderFacetContainers(facets) {
    for (const [facetName, container] of this.facetContainers) {
      // Remove only previously rendered facet wrapper, keep existing content (e.g. View Results button)
      const wrapper = container.querySelector('.plp-c-filter__facet-container');

      const isPriceFacet = facetName.startsWith('price') && !facetName.includes('compareAtPrice');
      const facet = facets.find((f) => f.name === facetName && (isPriceFacet || (!f.hidden && f.options?.length)));
      if (!facet) continue;

      // Clean facet content previously
      wrapper.innerHTML = '';

      if (isPriceFacet) {
        this.#renderPriceOptions(facet, wrapper);
      } else if (facetName === 'color_family') {
        this.#renderColorOptions(facet, wrapper);
      } else if (facetName === 'size') {
        this.#renderSizeOptions(facet, wrapper);
      } else {
        this.#renderCheckboxOptions(facet, wrapper);
      }
    }
  }

  /**
   * Restore previously selected filters after a re-render
   */
  #restoreSelections() {
    const containers = [this.filtersContainer, ...this.facetContainers.values()];

    for (const [facetName, values] of Object.entries(this.selectedFilters)) {
      const escapedName = CSS.escape(facetName);

      values.forEach((value) => {
        const escapedValue = CSS.escape(value);

        containers.forEach((container) => {
          if (!container) return;

          // Checkboxes
          const checkbox = container.querySelector(
            `.plp-c-filter__checkbox[data-facet-name="${escapedName}"][data-facet-value="${escapedValue}"]`,
          );
          if (checkbox) {
            checkbox.checked = true;
            return;
          }

          // Size buttons
          const sizeBtn = container.querySelector(
            `.plp-c-filter__size[data-facet-name="${escapedName}"][data-facet-value="${escapedValue}"]`,
          );
          if (sizeBtn) {
            sizeBtn.classList.add('plp-c-filter__size--selected');
            return;
          }

          // Color swatches
          const colorWrapper = container.querySelector(
            `.plp-c-filter__color-swatch-wrapper[data-facet-name="${escapedName}"][data-facet-value="${escapedValue}"]`,
          );
          if (colorWrapper) {
            colorWrapper
              .querySelector('.plp-c-filter__color-swatch')
              ?.classList.add('plp-c-filter__color-swatch--selected');
            colorWrapper.setAttribute('aria-pressed', 'true');
          }
        });
      });

      // Restore disabled states for price facets
      if (facetName.startsWith('price')) this.#updatePriceDisabledStates(facetName);
    }
  }

  /**
   * Render color facet as a 2-column grid of circular swatches.
   * Each swatch maps to a CSS class (isp-product-color-swatch-{color}) for its background color.
   * Clicking a swatch toggles the `plp-c-filter__color-swatch--selected` state and emits a filter change.
   * @param {Object} facet - The color facet object from Constructor API
   * @param {HTMLElement} wrapper - The container element to append color swatches into
   */
  #renderColorOptions(facet, wrapper) {
    wrapper.classList.add('plp-c-filter__color-swatches');

    facet.options.forEach((option) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'plp-c-filter__color-swatch-wrapper';
      item.dataset.facetName = facet.name;
      item.dataset.facetValue = option.value;
      item.setAttribute('aria-label', `Select color ${option.display_name}`);
      item.setAttribute('aria-pressed', 'false');
      this.displayNameMap.set(`${facet.name}:${option.value}`, option.display_name);

      const colorKey = option.value.toLowerCase().replace(/\s+/g, '_');
      const colorData = this.colorFamilies[colorKey];
      const swatch = document.createElement('span');
      swatch.className = 'plp-c-filter__color-swatch';

      if (colorData?.image) {
        swatch.style.backgroundImage = `url(${colorData.image})`;
        swatch.style.backgroundSize = 'cover';
        swatch.style.backgroundPosition = 'center';
      } else if (colorData?.hex) {
        swatch.style.backgroundColor = colorData.hex;
      }

      // Add border to pure white swatches so they stand out against the background
      if (!colorData?.image && colorData?.hex?.toLowerCase() === '#ffffff') {
        swatch.style.border = '1px solid #d3d5d8';
      }

      const nameCount = document.createElement('span');
      nameCount.className = 'plp-c-filter__color-name';
      nameCount.innerHTML = `${option.display_name} <span>(${option.count})</span>`;

      item.appendChild(swatch);
      item.appendChild(nameCount);

      item.addEventListener('click', () => {
        const selected = swatch.classList.toggle('plp-c-filter__color-swatch--selected');
        item.setAttribute('aria-pressed', String(selected));
        this.#syncSelection(facet.name, option.value, selected);
        this.#emitChange(facet.name);

        // Defer no user-visible tasks
        setTimeout(() => {
          if (selected) {
            filterOptionClickTracking('Color Family', option.value);
          }
        }, 0);
      });

      wrapper.appendChild(item);
    });
  }

  /**
   * Render size facet as a grid of toggle buttons.
   * Uses `plp-c-filter__sizes-numbers` for numeric sizes (e.g. 2, 4, 6) or `plp-c-filter__sizes-words` for text sizes (e.g. S, M, L).
   * Clicking a button toggles the `plp-c-filter__size--selected` state and emits a filter change.
   * @param {Object} facet - The size facet object from Constructor API
   * @param {HTMLElement} wrapper - The container element to append size buttons into
   */
  #renderSizeOptions(facet, wrapper) {
    const letterSizes = [];
    const doubleSizes = [];
    const numberSizes = [];

    facet.options.forEach((option) => {
      if (
        !option.display_name.includes('.') &&
        !option.display_name.includes('/') &&
        isNaN(parseFloat(option.display_name))
      ) {
        letterSizes.push(option);
      }
      if (option.display_name.includes('/')) {
        doubleSizes.push(option);
      }
      if (!isNaN(parseFloat(option.display_name))) {
        numberSizes.push(option);
      }
    });

    const createSizeBtn = (option) => {
      this.displayNameMap.set(`${facet.name}:${option.value}`, option.display_name);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'plp-c-filter__size';
      btn.textContent = option.display_name;
      btn.dataset.facetName = facet.name;
      btn.dataset.facetValue = option.value;

      btn.addEventListener('click', () => {
        const selected = btn.classList.toggle('plp-c-filter__size--selected');
        this.#syncSelection(facet.name, option.value, selected);
        this.#emitChange(facet.name);

        // Defer no user-visible tasks
        setTimeout(() => {
          if (selected) {
            filterOptionClickTracking('Size', option.value);
          }
        }, 0);
      });

      return btn;
    };

    if (letterSizes.length) {
      const wordsWrapper = document.createElement('div');
      wordsWrapper.className = 'plp-c-filter__sizes-words';
      letterSizes
        .sort((a, b) => {
          const ai = SIZE_ORDER.get(a.display_name.toUpperCase());
          const bi = SIZE_ORDER.get(b.display_name.toUpperCase());
          if (ai != null && bi != null) return ai - bi;
          if (ai != null) return -1;
          if (bi != null) return 1;
          return a.display_name.localeCompare(b.display_name);
        })
        .forEach((option) => wordsWrapper.appendChild(createSizeBtn(option)));
      wrapper.appendChild(wordsWrapper);
    }

    if (doubleSizes.length) {
      const doubleWrapper = document.createElement('div');
      doubleWrapper.className = 'plp-c-filter__sizes-doubles';
      doubleSizes.forEach((option) => doubleWrapper.appendChild(createSizeBtn(option)));
      wrapper.appendChild(doubleWrapper);
    }

    if (numberSizes.length) {
      const numbersWrapper = document.createElement('div');
      numbersWrapper.className = 'plp-c-filter__sizes-numbers';
      numberSizes
        .sort((a, b) => parseFloat(a.display_name) - parseFloat(b.display_name))
        .forEach((option) => numbersWrapper.appendChild(createSizeBtn(option)));
      wrapper.appendChild(numbersWrapper);
    }
  }

  /**
   * Render a generic facet as a list of checkboxes.
   * Used as the default renderer for all facets that are not color or size (e.g. Neckline, Occasion, Material).
   * Each option displays a checkbox with its name and count.
   * @param {Object} facet - The facet object from Constructor API
   * @param {HTMLElement} wrapper - The container element to append checkbox options into
   */
  #renderCheckboxOptions(facet, wrapper) {
    const needsNumericSort = facet.name === 'reviews_average' || facet.name.startsWith('price');
    const options = needsNumericSort
      ? [...facet.options].sort((a, b) => parseFloat(a.display_name) - parseFloat(b.display_name))
      : facet.options;

    options.forEach((option) => {
      const wrap = document.createElement('div');
      wrap.className = 'plp-c-filter__checkbox-wrap';

      const value = facet.name.startsWith('price') ? option.value.replace(/"/g, '') : option.value;

      this.displayNameMap.set(`${facet.name}:${value}`, option.display_name);

      const checkboxId = `filter-${facet.name}-${value}`.replace(/\s+/g, '-');

      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.id = checkboxId;
      checkbox.className = 'plp-c-filter__checkbox';
      checkbox.dataset.facetName = facet.name;
      checkbox.dataset.facetValue = value;

      const label = document.createElement('label');
      label.className = 'plp-c-filter__checkbox-label';
      label.htmlFor = checkboxId;
      label.innerHTML = `${option.display_name} <span>(${option.count})</span>`;

      checkbox.addEventListener('change', () => {
        this.#syncSelection(facet.name, value, checkbox.checked);
        this.#emitChange(facet.name);

        // Defer no user-visible tasks
        setTimeout(() => {
          const category = facet.name === 'reviews_average' ? 'Isp-reviews-stars' : `Isp-tag-${facet.display_name}`;
          const subcategory = facet.name === 'reviews_average' ? option.display_name.replace('+', '') : value;
          if (checkbox.checked) {
            filterOptionClickTracking(category, subcategory);
          }
        }, 0);
      });

      wrap.appendChild(checkbox);
      wrap.appendChild(label);
      wrapper.appendChild(wrap);
    });
  }

  /**
   * Sync the visual selection state of a facet option across all containers
   * (sidebar + registered facet containers) so that #emitChange collects consistently.
   * @param {string} facetName
   * @param {string} facetValue
   * @param {boolean} selected
   */
  #syncSelection(facetName, facetValue, selected) {
    const escapedName = CSS.escape(facetName);
    const escapedValue = CSS.escape(facetValue);
    const containers = [this.filtersContainer, ...this.facetContainers.values()];

    containers.forEach((container) => {
      if (!container) return;

      // Checkboxes
      const checkbox = container.querySelector(
        `.plp-c-filter__checkbox[data-facet-name="${escapedName}"][data-facet-value="${escapedValue}"]`,
      );
      if (checkbox) {
        checkbox.checked = selected;
        return;
      }

      // Size buttons
      const sizeBtn = container.querySelector(
        `.plp-c-filter__size[data-facet-name="${escapedName}"][data-facet-value="${escapedValue}"]`,
      );
      if (sizeBtn) {
        sizeBtn.classList.toggle('plp-c-filter__size--selected', selected);
        return;
      }

      // Color swatches
      const colorWrapper = container.querySelector(
        `.plp-c-filter__color-swatch-wrapper[data-facet-name="${escapedName}"][data-facet-value="${escapedValue}"]`,
      );
      if (colorWrapper) {
        colorWrapper
          .querySelector('.plp-c-filter__color-swatch')
          ?.classList.toggle('plp-c-filter__color-swatch--selected', selected);
        colorWrapper.setAttribute('aria-pressed', String(selected));
      }
    });
  }

  /**
   * Build 1–6 overlapping [min,max] price groups whose widths increase exponentially.
   * @param {number} min - minimum price (float or whole number, >= 1 before rounding)
   * @param {number} max - maximum price (float or whole number, > min before rounding)
   * @returns {Array<[number, number]>} array of [groupMin, groupMax] pairs
   *
   * Inputs are rounded to whole numbers (min floors down, max ceils up).
   * The number of groups (1–6) scales with the spread between min and max,
   * and is capped to prevent empty or reversed groups on narrow ranges.
   */
  #exponentialGroups(min, max) {
    if (min == null || min === false || max == null || max === false) return [0, 0];

    min > max && ([min, max] = [max, min]);

    min = Math.floor(min);
    max = Math.ceil(max);

    if (min < 1 || max <= min) return [[min, max]];

    const range = max - min;

    let groups;
    if (range <= 10) groups = 2;
    else if (range <= 25) groups = 3;
    else if (range <= 50) groups = 4;
    else if (range <= 100) groups = 5;
    else groups = 6;

    groups = Math.min(groups, range <= 2 ? 1 : range);

    const p = groups === 2 ? 1 : 2;

    if (groups === 1) return [[min, max]];

    const boundaries = new Array(groups + 1);
    boundaries[0] = min;
    boundaries[groups] = max;

    for (let i = 1; i < groups; i++) {
      const t = i / groups;
      let b = Math.floor(min + range * Math.pow(t, p));
      const prev = boundaries[i - 1];
      const remaining = groups - i;
      b = Math.max(b, prev + 1);
      b = Math.min(b, max - remaining);
      boundaries[i] = b;
    }

    const result = [];
    for (let i = 0; i < groups; i++) {
      result.push([boundaries[i], boundaries[i + 1]]);
    }
    return result;
  }

  /**
   * Render price range facet as checkboxes.
   * US: fixed predefined ranges from US_PRICE_RANGES, disabled based on API min/max.
   * International: exponentially spaced groups built from API min/max.
   * @param {Object} facet - The range facet object from Constructor API (has min/max, no options)
   * @param {HTMLElement} wrapper - The container element to append checkboxes into
   */
  #renderPriceOptions(facet, wrapper) {
    const isUS = this.country === 'US';

    let min = facet.min;
    let max = facet.max;

    if (min && max) {
      savePriceCache(this.country, min, max);
    } else {
      const cached = getPriceCache(this.country);
      if (cached) {
        min = cached.min;
        max = cached.max;
      }
    }

    if (!min || !max) return;

    min = Math.ceil(min);
    max = Math.floor(max);
    if (max <= 1) return;

    let allRanges;
    let disabledSet;

    if (isUS) {
      allRanges = US_PRICE_RANGES.map((range) => {
        const rangeMax = range.max === Infinity ? max : range.max;
        return {
          min: range.min,
          max: rangeMax,
          label: range.max === Infinity ? `$${range.min} +` : range.label,
        };
      });

      disabledSet = new Set();
      allRanges.forEach((range, index) => {
        const overlaps = range.max >= min && range.min <= max;
        if (!overlaps) disabledSet.add(index);
      });
    } else {
      const groups = this.#exponentialGroups(min, max);
      const currencySymbol = window.theme?.currency_symbol || '$';
      const currencyIso = window.theme?.currency_iso || 'USD';
      const currencyPrefix =
        currencySymbol === '$' && currencyIso !== 'USD' && currencyIso !== 'HKD' ? `${currencyIso} ` : '';

      const renderedValues = new Set(groups.map(([gMin, gMax]) => `${gMin}-${gMax}`));
      const selectedPrices = this.selectedFilters[facet.name] || [];

      const intlRanges = [...groups];
      selectedPrices.forEach((value) => {
        if (renderedValues.has(value)) return;
        const [valMin, valMax] = value.split('-');
        intlRanges.push([Number(valMin), Number(valMax)]);
      });

      intlRanges.sort((a, b) => a[0] - b[0] || a[1] - b[1]);

      allRanges = intlRanges.map(([rMin, rMax]) => ({
        min: rMin,
        max: rMax,
        label: `${currencyPrefix}${currencySymbol}${rMin} - ${currencyPrefix}${currencySymbol}${rMax}`,
      }));
    }

    allRanges.forEach((range, index) => {
      const value = `${range.min}-${range.max}`;
      const isQuick = wrapper.dataset.quickFilter === 'true';
      const checkboxId = `${isQuick ? 'quick-' : ''}filter-${facet.name}-${value}`;
      const wrap = document.createElement('div');
      wrap.className = 'plp-c-filter__checkbox-wrap';

      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.className = 'plp-c-filter__checkbox';
      checkbox.id = checkboxId;
      checkbox.dataset.facetName = facet.name;
      checkbox.dataset.facetValue = value;
      checkbox.dataset.priceIndex = index;

      if (disabledSet?.has(index)) {
        checkbox.disabled = true;
        checkbox.dataset.outOfRange = 'true';
        wrap.classList.add('plp-c-filter__checkbox-wrap--disabled');
      }

      const label = document.createElement('label');
      label.className = 'plp-c-filter__checkbox-label';
      label.htmlFor = checkboxId;
      label.textContent = range.label;

      checkbox.addEventListener('change', this.#priceCheckboxHandler.bind(this));

      wrap.appendChild(checkbox);
      wrap.appendChild(label);
      wrapper.appendChild(wrap);
    });

    this.#updatePriceDisabledStates(facet.name);
  }

  /**
   * Event handler for price checkbox changes.
   * @param {EventTarget} e - Input event from a price checkbox change
   * @returns {void} - end of event handler
   */
  #priceCheckboxHandler(e) {
    const checkbox = e.currentTarget;
    const facetValue = checkbox.dataset.facetValue;
    const facetName = checkbox.dataset.facetName;

    // Uncheck all checked checkboxes from any one marked as center
    if (checkbox.classList.contains('is_center')) {
      checkbox.classList.remove('is_center');
      this.#syncSelection(facetName, facetValue, checkbox.checked);

      // Look for all checked checkboxes across all containers that contain price checkboxes
      // Keep in mind, there are two, one for quick price filter (mobile) and one for sidebar (mobile & desktop)
      checkbox
        .closest(`details[data-facet-name="${facetName}"], div.plp-c-filter__facet-container`)
        .querySelectorAll(`.plp-c-filter__checkbox[data-facet-name="${facetName}"][data-price-index]`)
        .forEach((cb) => {
          if (cb.checked) {
            cb.checked = false;
            this.#syncSelection(facetName, cb.dataset.facetValue, false);
          }
        });

      // Send zero price ranges selected
      this.#emitChange(facetName);
      return;
    }

    this.#syncSelection(facetName, facetValue, checkbox.checked);
    this.#updatePriceDisabledStates(facetName);
    this.#emitChange(facetName);

    // Defer no user-visible tasks
    setTimeout(() => {
      if (checkbox.checked) {
        const [min, max] = facetValue.split('-').map(Number);
        const range = { min, max };
        filterOptionClickTracking('Price', `${this.country}:${range.min}.0-${this.country}:${range.max}.0`);
      }
    }, 0);
    return;
  }

  /**
   * Disable price checkboxes that are 2+ groups away from the current contiguous selection.
   * Syncs disabled state across all containers (sidebar + mobile modals).
   * @param {string} facetName - the price facet name
   */
  #updatePriceDisabledStates(facetName) {
    const escapedName = CSS.escape(facetName);
    const containers = [this.filtersContainer, ...this.facetContainers.values()];

    // Collect all price checkboxes and find the selected index range
    let minSelected = Infinity;
    let maxSelected = -Infinity;

    containers.forEach((container) => {
      if (!container) return;
      container
        .querySelectorAll(`.plp-c-filter__checkbox[data-facet-name="${escapedName}"][data-price-index]`)
        .forEach((cb) => {
          if (cb.checked) {
            const idx = Number(cb.dataset.priceIndex);
            if (idx < minSelected) minSelected = idx;
            if (idx > maxSelected) maxSelected = idx;
          }
        });
    });

    const hasSelection = minSelected !== Infinity;

    containers.forEach((container) => {
      if (!container) return;
      container
        .querySelectorAll(`.plp-c-filter__checkbox[data-facet-name="${escapedName}"][data-price-index]`)
        .forEach((cb) => {
          if (cb.dataset.outOfRange === 'true') return;
          const idx = Number(cb.dataset.priceIndex);
          const disabled = hasSelection && (idx < minSelected - 1 || idx > maxSelected + 1);
          const iscenter = hasSelection && minSelected < idx && idx < maxSelected;
          cb.disabled = disabled;
          cb.classList.toggle('is_center', iscenter);
          cb.closest('.plp-c-filter__checkbox-wrap')?.classList.toggle(
            'plp-c-filter__checkbox-wrap--disabled',
            disabled,
          );
        });
    });
  }

  /**
   * Merge contiguous price selections into a single min-max range for the API.
   * Mutates the filters object in place.
   * @param {Object} filters - the active filters object
   */
  #mergePriceFilters(filters) {
    for (const name of Object.keys(filters)) {
      if (!name.startsWith('price') || filters[name].length <= 1) continue;

      let rangeMin = Infinity;
      let rangeMax = -Infinity;
      filters[name].forEach((value) => {
        const [lo, hi] = value.split('-').map(Number);
        if (lo < rangeMin) rangeMin = lo;
        if (hi > rangeMax) rangeMax = hi;
      });

      filters[name] = [`${rangeMin}-${rangeMax}`];
    }
  }

  /**
   * Collect all active filter selections and invoke the onChange callback
   * @param {string} [facetName] - name of the facet that changed, used to anchor scroll position
   */
  #emitChange(facetName) {
    if (facetName) {
      const section = this.filtersContainer.querySelector(`[data-facet-name="${facetName}"]`);
      if (section) {
        this.scrollAnchor = { facetName, offsetFromViewport: section.getBoundingClientRect().top };
      }
    }

    const filters = {};
    const containers = [this.filtersContainer, ...this.facetContainers.values()];

    containers.forEach((container) => {
      if (!container) return;

      container.querySelectorAll('.plp-c-filter__checkbox:checked').forEach((cb) => {
        const name = cb.dataset.facetName;
        if (!filters[name]) filters[name] = [];
        if (!filters[name].includes(cb.dataset.facetValue)) filters[name].push(cb.dataset.facetValue);
      });

      container.querySelectorAll('.plp-c-filter__size.plp-c-filter__size--selected').forEach((btn) => {
        const name = btn.dataset.facetName;
        if (!filters[name]) filters[name] = [];
        if (!filters[name].includes(btn.dataset.facetValue)) filters[name].push(btn.dataset.facetValue);
      });

      container
        .querySelectorAll('.plp-c-filter__color-swatch-wrapper:has(.plp-c-filter__color-swatch--selected)')
        .forEach((wrapper) => {
          const name = wrapper.dataset.facetName;
          if (!filters[name]) filters[name] = [];
          if (!filters[name].includes(wrapper.dataset.facetValue)) filters[name].push(wrapper.dataset.facetValue);
        });
    });

    // Merge with previously selected values that may no longer be in the DOM
    // (the API narrows facet options, hiding still-active selections).
    // For the facet the user just changed, trust the DOM as source of truth.
    for (const [name, values] of Object.entries(this.selectedFilters)) {
      if (name === facetName) continue; // user changed this facet — use DOM state
      if (!filters[name]) filters[name] = [];
      values.forEach((v) => {
        if (!filters[name].includes(v)) filters[name].push(v);
      });
    }

    // Clean up empty arrays
    for (const name of Object.keys(filters)) {
      if (!filters[name].length) delete filters[name];
    }

    this.selectedFilters = filters;
    this.#updateSelectedCounts();
    this.#renderRemovableTags();

    // Send a merged price range to the API (single value) while keeping
    // individual group selections in selectedFilters for the UI
    const apiFilters = { ...filters };
    this.#mergePriceFilters(apiFilters);
    this.onChange?.(apiFilters, filters);
  }

  /**
   * Render removable tag pills into the sidebar and mobile containers
   * based on the current selectedFilters state.
   */
  #renderRemovableTags() {
    const targets = [
      { wrapper: this.sidebarTagsWrapper, container: this.sidebarTagsContainer },
      { wrapper: this.mobileTagsWrapper, container: this.mobileTagsContainer },
    ];

    const entries = Object.entries(this.selectedFilters);
    const hasFilters = entries.some(([, values]) => values.length > 0);

    targets.forEach(({ wrapper, container }) => {
      if (!wrapper || !container) return;

      container.innerHTML = '';
      wrapper.style.display = hasFilters ? '' : 'none';

      if (!hasFilters) return;

      entries.forEach(([facetName, values]) => {
        const isPrice = facetName.startsWith('price');
        values.forEach((value) => {
          let displayValue = this.displayNameMap.get(`${facetName}:${value}`) || value;
          if (isPrice && value.includes('-')) {
            const currencySymbol = window.theme?.currency_symbol || '$';
            const currencyIso = window.theme?.currency_iso || 'USD';
            const currencyPrefix =
              currencySymbol === '$' && currencyIso !== 'USD' && currencyIso !== 'HKD' ? `${currencyIso} ` : '';
            const [min, max] = value.split('-');
            displayValue = `${currencyPrefix}${currencySymbol}${min.replace(
              /\D/g,
              '',
            )} - ${currencyPrefix}${currencySymbol}${max.replace(/\D/g, '')}`;
          }
          const tag = document.createElement('button');
          tag.type = 'button';
          tag.className = 'plp-c-filter__removable-tag';
          tag.innerHTML = `<span>${displayValue}</span><i class="fa fa-times"></i>`;
          tag.addEventListener('click', () => this.#removeFilter(facetName, value));
          container.appendChild(tag);
        });
      });
    });

    // Toggle active state on mobile filter buttons
    this.#updateMobileFilterButtons();
  }

  /**
   * Update the "N selected" badge on each facet summary.
   * CSS handles visibility: shown when collapsed + has selections, hidden otherwise.
   */
  #updateSelectedCounts() {
    if (!this.filtersContainer) return;

    this.filtersContainer.querySelectorAll('.plp-c-filter__section').forEach((details) => {
      const facetName = details.dataset.facetName;
      const badgeEl = details.querySelector('.plp-c-filter__selected-count-badge');
      if (!badgeEl) return;

      const selectedCount = this.selectedFilters[facetName]?.length || 0;

      if (selectedCount > 0) {
        badgeEl.textContent = selectedCount;
        details.dataset.selectedCount = selectedCount;
      } else {
        delete details.dataset.selectedCount;
      }
    });
  }

  /**
   * Toggle the is-active class on mobile filter buttons based on active selections.
   * Price button uses a startsWith('price') match since the facet name is country-specific.
   */
  #updateMobileFilterButtons() {
    const ACTIVE_CLASS = 'plp-c-mobile-filters__filter-btn-is-active';
    const DEDICATED_FACETS = new Set(['size', 'color_family']);

    document.querySelectorAll('[data-mobile-filter-facet]').forEach((btn) => {
      const facet = btn.dataset.mobileFilterFacet;
      const isActive =
        facet === 'price'
          ? Object.keys(this.selectedFilters).some(
              (name) => name.startsWith('price') && this.selectedFilters[name].length,
            )
          : this.selectedFilters[facet]?.length > 0;
      btn.classList.toggle(ACTIVE_CLASS, isActive);
    });

    // "All Filters" button: highlight when any non-dedicated facet has selections
    const allFiltersBtn = document.querySelector('#mobileOpenAll');
    if (allFiltersBtn) {
      const hasOtherFilters = Object.entries(this.selectedFilters).some(
        ([name, values]) => !DEDICATED_FACETS.has(name) && !name.startsWith('price') && values.length > 0,
      );
      allFiltersBtn.classList.toggle(ACTIVE_CLASS, hasOtherFilters);
    }
  }

  /**
   * Remove a single filter value and re-emit the change
   * @param {string} facetName - the facet to remove from
   * @param {string} value - the specific value to deselect
   */
  #removeFilter(facetName, value) {
    // Deselect the UI element across all containers
    this.#syncSelection(facetName, value, false);

    // Directly update selectedFilters instead of re-collecting from DOM,
    // because narrowed API responses may have removed some options from the DOM
    // which would cause #emitChange to lose those selections.
    if (this.selectedFilters[facetName]) {
      this.selectedFilters[facetName] = this.selectedFilters[facetName].filter((v) => v !== value);
      if (!this.selectedFilters[facetName].length) delete this.selectedFilters[facetName];
    }

    // Update price disabled states after removing a price group
    if (facetName.startsWith('price')) this.#updatePriceDisabledStates(facetName);

    // Preserve sidebar scroll position so renderFacets can restore it after re-render
    const sidebar = this.filtersContainer?.closest('aside');
    if (sidebar) this.savedScrollTop = sidebar.scrollTop;

    this.#updateSelectedCounts();
    this.#renderRemovableTags();
    const apiFilters = { ...this.selectedFilters };
    this.#mergePriceFilters(apiFilters);
    this.onChange?.(apiFilters, { ...this.selectedFilters });
  }

  /**
   * Get the set of facet names the user has manually closed, from localStorage.
   * @returns {Set<string>}
   */
  #getClosedFacets() {
    try {
      const raw = localStorage.getItem('plp_closed_facets');
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  }

  /**
   * Persist the closed facets set to localStorage.
   */
  #saveClosedFacets() {
    try {
      localStorage.setItem('plp_closed_facets', JSON.stringify([...this.closedFacets]));
    } catch {
      // silent
    }
  }

  /**
   * Parse color families metaobject data from the embedded JSON script tag.
   * @returns {Object} map of color key → { hex, image }
   */
  #parseColorFamilies() {
    try {
      const script = document.querySelector('script[data-color-families]');
      return script ? JSON.parse(script.textContent) : {};
    } catch {
      return {};
    }
  }

  /**
   * Clear all active filters and re-emit the change
   */
  #clearAllFilters() {
    const containers = [this.filtersContainer, ...this.facetContainers.values()];

    containers.forEach((container) => {
      if (!container) return;
      container.querySelectorAll('.plp-c-filter__checkbox:checked').forEach((cb) => (cb.checked = false));
      container
        .querySelectorAll('.plp-c-filter__size--selected')
        .forEach((btn) => btn.classList.remove('plp-c-filter__size--selected'));
      container
        .querySelectorAll('.plp-c-filter__color-swatch--selected')
        .forEach((swatch) => swatch.classList.remove('plp-c-filter__color-swatch--selected'));
    });

    this.selectedFilters = {};
    this.#renderRemovableTags();
    this.onChange?.({}, {});

    // Defer no visible-user tasks
    setTimeout(() => {
      filterClearTracking();
    }, 0);
  }
}
